import {
	IntervalOption,
	PatternPackOptionsValue,
	RegimenDurationOption,
	RegimenScheduleOption,
	RegimenWeightBasedOption,
	TimeUnitOption,
} from "../../../../models/simulator/options";
import { getFinancialData } from "./../financial-service/financial.service";
import { IProductPricingInput, IPatientWeightInput, ITreatmentRegimenInput } from "./../inputs";
import { getLastElement, groupBy, partition } from "@lyfegen-platform/utils";
import {
	addDays,
	addMonths,
	addWeeks,
	addYears,
	compareAsc,
	differenceInDays,
	differenceInWeeks,
	endOfMonth,
	isAfter,
	isBefore,
	isSameMonth,
	isWithinInterval,
	min,
	subDays,
} from "date-fns";
import { constructNameForWeightGroup, roundToTwo } from "./../utils";
import { getDiscontinuedPatients } from "./../patient-service";
import {
	ICreateMonthlyDistributionArgs,
	ICreatePackEntryForEveryDurationArgs,
	ICreatePackEntryForFirstDurationArgs,
	ICreateWeeklyDistributionsArgs,
	IDistributePacksArgs,
	IDistributedPack,
	IGetDistributedPacksArgs,
	IGetPacksByWeightGroups,
	IPackEntry,
	IReusablePackTracker,
} from "./interfaces";
import { GROUP_LABEL } from "../model-service";
import Decimal from "decimal.js";
import { toFixedNumber } from "../../details/output/functions";

const maxEndDate = "9999-12-31";

export const createPackEntryForFirstDuration = (
	args: ICreatePackEntryForFirstDurationArgs
): { distributedPackForFirstDuration: IPackEntry[] | null; reusablePackTracker?: IReusablePackTracker[] } => {
	const { treatmentRegimen, currentDate, lastAdministration, numberOfPatients, isWeightCohort } = args;

	if (lastAdministration) return { distributedPackForFirstDuration: null };

	const administrationDate = getAdministrationDate(lastAdministration, args.startOfDistribution, currentDate, 0);
	if (treatmentRegimen.pack.name === RegimenWeightBasedOption.MG_KG_OF_PATIENT_WEIGHT && treatmentRegimen.pattern) {
		const { distributedPacks, packTracker } = getPacksByWeightGroups({
			weight: { weightGroups: args.averageWeightPerGroup, isWeightCohort },
			treatmentRegimen,
			allPacks: args.allPacks,
			currentDate,
			lastAdministrationDate: currentDate,
			numberOfPatients: numberOfPatients,
			previousMonthPackTracker: args.previousMonthPackTracker,
			administrationDates: [administrationDate],
		});

		return { distributedPackForFirstDuration: distributedPacks, reusablePackTracker: packTracker };
	}

	return {
		distributedPackForFirstDuration: [
			{
				packName: treatmentRegimen.pack.name,
				quantity: treatmentRegimen.pack.quantity * numberOfPatients,
				administrationDate: currentDate,
			},
		],
	};
};

export const createMonthlyDistribution = (args: ICreateMonthlyDistributionArgs) => {
	const { currentDate, treatmentRegimen, lastAdministration, numberOfPatients, endDate, isWeightCohort } = args;

	const newAdministration = getAdministrationDate(
		lastAdministration,
		args.startOfDistribution,
		currentDate,
		+treatmentRegimen.schedule.value
	);

	const isReusingPack =
		treatmentRegimen.pack.name === RegimenWeightBasedOption.MG_KG_OF_PATIENT_WEIGHT &&
		treatmentRegimen.pattern?.pattern === PatternPackOptionsValue.REUSE_PACK;

	if ((!isSameMonth(newAdministration, currentDate) && !isReusingPack) || (endDate && isAfter(newAdministration, endDate))) {
		return { distributedPackForEveryDuration: null };
	}

	if (treatmentRegimen.pack.name === RegimenWeightBasedOption.MG_KG_OF_PATIENT_WEIGHT && treatmentRegimen.pattern) {
		const result = getPacksByWeightGroups({
			weight: { weightGroups: args.averageWeightPerGroup, isWeightCohort },
			treatmentRegimen,
			allPacks: args.allPacks,
			currentDate: newAdministration,
			administrationDates: [newAdministration],
			lastAdministrationDate: currentDate,
			numberOfPatients,
			previousMonthPackTracker: args.previousMonthPackTracker,
		});

		return { distributedPackForEveryDuration: result.distributedPacks, reusablePackTracker: result.packTracker };
	}

	return {
		distributedPackForEveryDuration: [
			{
				packName: treatmentRegimen.pack.name,
				quantity: treatmentRegimen.pack.quantity * numberOfPatients,
				administrationDate: newAdministration,
			},
		],
	};
};

export const createDistributions = (args: ICreateWeeklyDistributionsArgs) => {
	const {
		currentDate,
		treatmentRegimen,
		lastAdministration,
		numberOfPatients,
		endDate,
		isWeightCohort,
		averageWeightPerGroup,
		allPacks,
		previousMonthPackTracker,
	} = args;
	const durationHelpers =
		treatmentRegimen.schedule.unit === TimeUnitOption.WEEK
			? {
					addDuration: addWeeks,
					differenceInDuration: differenceInWeeks,
			  }
			: {
					addDuration: addDays,
					differenceInDuration: differenceInDays,
			  };

	const endOfCurrentMonth = endDate && isSameMonth(endDate, currentDate) ? endDate : endOfMonth(currentDate);
	if (
		lastAdministration &&
		compareAsc(durationHelpers.addDuration(lastAdministration, +treatmentRegimen.schedule.value), endOfCurrentMonth) > 0
	)
		return { distributedPackForEveryDuration: null };

	const startTreatmentOffset = lastAdministration ? 0 : 1;
	const actualLastAdministration = lastAdministration ?? currentDate;
	const availableWeeksOrDaysForTreatment = durationHelpers.differenceInDuration(endOfCurrentMonth, actualLastAdministration);

	const numberOfTreatmentsApplied =
		Math.floor(availableWeeksOrDaysForTreatment / +treatmentRegimen.schedule.value) + startTreatmentOffset;

	const baseDate = lastAdministration ? durationHelpers.addDuration(lastAdministration, +treatmentRegimen.schedule.value) : currentDate;

	const administrationDates: Date[] = [];
	for (let i = 0; i < numberOfTreatmentsApplied; i++) {
		const nextDate = i === 0 ? baseDate : durationHelpers.addDuration(baseDate, +treatmentRegimen.schedule.value * i);
		administrationDates.push(nextDate);
	}

	const isWeightBased = treatmentRegimen.pack.name === RegimenWeightBasedOption.MG_KG_OF_PATIENT_WEIGHT && treatmentRegimen.pattern;

	if (isWeightBased && treatmentRegimen.pattern!.pattern === PatternPackOptionsValue.REUSE_PACK) {
		const { distributedPacks, packTracker } = getPacksByWeightGroups({
			weight: { weightGroups: averageWeightPerGroup, isWeightCohort },
			treatmentRegimen,
			allPacks,
			administrationDates,
			currentDate,
			lastAdministrationDate: durationHelpers.addDuration(
				actualLastAdministration,
				(numberOfTreatmentsApplied - startTreatmentOffset) * +treatmentRegimen.schedule.value
			),
			numberOfPatients,
			previousMonthPackTracker,
		});

		return {
			distributedPackForEveryDuration: distributedPacks,
			reusablePackTracker: packTracker,
		};
	}

	let weightPacksForSingleDose: IPackEntry[] | undefined;
	let packTracker: IReusablePackTracker[] | undefined;

	if (isWeightBased) {
		const { distributedPacks, packTracker: tracker } = getPacksByWeightGroups({
			weight: { weightGroups: averageWeightPerGroup, isWeightCohort },
			treatmentRegimen,
			allPacks,
			administrationDates: [],
			currentDate,
			lastAdministrationDate: currentDate,
			numberOfPatients,
			previousMonthPackTracker,
		});
		weightPacksForSingleDose = distributedPacks;
		packTracker = tracker;
	}

	const distributedPackForEveryDuration = distributePacks({
		weightPacks: weightPacksForSingleDose,
		treatmentRegimen,
		numberOfTreatmentsApplied: administrationDates.length,
		lastAdministration,
		currentDate,
		actualLastAdministration,
		numberOfPatients,
		addDuration: durationHelpers.addDuration,
		administrationDates,
	});

	return {
		distributedPackForEveryDuration,
		reusablePackTracker: packTracker,
	};
};

export function buildAdministrationDates(
	baseDate: Date,
	treatmentsCount: number,
	scheduleValue: number,
	addDurationFn: (d: Date, amount: number) => Date
): Date[] {
	const dates: Date[] = [];
	for (let i = 0; i < treatmentsCount; i++) {
		const date = i === 0 ? baseDate : addDurationFn(baseDate, scheduleValue * i);
		if (compareAsc(date, endOfMonth(baseDate)) > 0) break;
		dates.push(date);
	}

	return dates;
}

interface IDistributePacks {
	weightPacks?: IPackEntry[];
	treatmentRegimen: ITreatmentRegimenInput;
	numberOfTreatmentsApplied: number;
	lastAdministration?: Date;
	currentDate: Date;
	actualLastAdministration: Date;
	numberOfPatients: number;
	addDuration: (date: Date | number, amount: number) => Date;
	administrationDates?: Date[];
}

const distributePacks = (args: IDistributePacks) => {
	const {
		weightPacks,
		treatmentRegimen,
		numberOfTreatmentsApplied,
		lastAdministration,
		currentDate,
		actualLastAdministration,
		numberOfPatients,
		addDuration,
		administrationDates,
	} = args;
	const packs: IPackEntry[] = [];

	if (administrationDates && administrationDates.length > 0) {
		for (const date of administrationDates) {
			if (weightPacks) {
				weightPacks.forEach((wp) =>
					packs.push({
						...wp,
						administrationDate: date,
					})
				);
			} else {
				packs.push({
					packName: treatmentRegimen.pack.name,
					quantity: treatmentRegimen.pack.quantity * numberOfPatients,
					administrationDate: date,
				});
			}
		}

		return packs;
	}

	for (let i = 1; i <= numberOfTreatmentsApplied; i++) {
		let administrationDate: Date;
		if (!lastAdministration) {
			administrationDate = i === 1 ? currentDate : addDuration(currentDate, +treatmentRegimen.schedule.value * (i - 1));
		} else {
			administrationDate = addDuration(actualLastAdministration, i * +treatmentRegimen.schedule.value);
		}

		if (!!weightPacks) {
			weightPacks.forEach((pack) =>
				packs.push({
					...pack,
					administrationDate,
				})
			);
		} else {
			packs.push({
				packName: treatmentRegimen.pack.name,
				quantity: treatmentRegimen.pack.quantity * numberOfPatients,
				administrationDate,
			});
		}
	}

	return packs;
};

export const createPackEntryForEveryDuration = (
	args: ICreatePackEntryForEveryDurationArgs
): { distributedPackForEveryDuration: IPackEntry[] | null; reusablePackTracker?: IReusablePackTracker[] } => {
	const { treatmentRegimen, endDate, currentDate } = args;
	if (endDate && isAfter(currentDate, endDate)) {
		return { distributedPackForEveryDuration: null };
	}

	if (treatmentRegimen.schedule.unit === TimeUnitOption.MONTH) {
		return createMonthlyDistribution(args);
	}

	return createDistributions(args);
};

export const getTreatmentPrice = (packName: string, administrationDate: Date, productPricing: Array<IProductPricingInput>): number => {
	const price =
		productPricing.find(
			(productPrice) =>
				productPrice.pack === packName &&
				isWithinInterval(administrationDate, {
					start: new Date(productPrice.timeframe.startDate),
					end: new Date(productPrice.timeframe.endDate ?? maxEndDate),
				})
		)?.timeframe.price || 0;

	return Number(price);
};

export const getPatientWeightFoDosagePurpose = (patientWeight: IPatientWeightInput[]) => {
	return patientWeight.map((group) => {
		if (group.endValue && group.startInterval === IntervalOption.FROM) {
			return { value: +(+group.startValue + +group.endValue).toFixed(2) / 2, share: group.share };
		}

		return { value: group.startValue, share: group.share };
	});
};

export const getPacksByWeightGroups = (args: IGetPacksByWeightGroups) => {
	const { weight, treatmentRegimen, allPacks, administrationDates, currentDate, numberOfPatients, previousMonthPackTracker } = args;
	const mgPerKg = treatmentRegimen.pack.quantity;
	const pattern = treatmentRegimen.pattern?.pattern;

	if (!pattern) {
		throw Error("Pack pattern is mandatory for weight based regimens");
	}

	if (pattern === PatternPackOptionsValue.REUSE_PACK) {
		const reusablePack = getReusablePackForWeightGroups(
			weight,
			mgPerKg,
			allPacks[0],
			administrationDates || [],
			numberOfPatients,
			previousMonthPackTracker
		);

		return {
			distributedPacks: reusablePack.sumOfPacksForAllTheWeightGroups as IPackEntry[],
			packTracker: reusablePack.packTracker,
		};
	}

	const groupsWithRegimenDosage = weight.weightGroups.map((group) => ({
		...group,
		regimenDosage: roundToTwo(group.value * mgPerKg),
	}));
	const groupsAndPacks = distributePacksByWeightDosage(
		groupsWithRegimenDosage,
		allPacks,
		currentDate,
		numberOfPatients,
		weight.isWeightCohort,
		pattern
	);

	const sumOfPacksForAllTheWeightGroups = sumPacks(groupsAndPacks);

	return { distributedPacks: sumOfPacksForAllTheWeightGroups };
};

export const getReusablePackForWeightGroups = (
	weight: { weightGroups: { key?: string; value: number; share: number }[]; isWeightCohort: boolean },
	mgPerKg: number,
	pack: string,
	administrationDates: Date[],
	numberOfPatients: number,
	previousMonthPackTracker?: IReusablePackTracker[]
) => {
	const groupsWithRegimenDosage = weight.weightGroups.map((group) => ({
		...group,
		regimenDosage: roundToTwo(group.value * mgPerKg),
		numberOfPatientsInGroup: weight.isWeightCohort
			? numberOfPatients
			: new Decimal(group.share).dividedBy(100).times(numberOfPatients).toNumber(),
		remainingDose: previousMonthPackTracker
			? previousMonthPackTracker.find((value: { weighGroup: number | string }) => {
					return value.weighGroup === (group.key || group.value);
			  })?.remainingDosage
			: 0,
	}));

	const groupsAndPacks = groupsWithRegimenDosage.map((group) => reusePackPatternEnumerated(group, pack, administrationDates));
	const sumOfPacksForAllTheWeightGroups = sumPacks(groupsAndPacks);
	const packTracker = groupsAndPacks.map((group) => group.remainingDose);

	return { sumOfPacksForAllTheWeightGroups, packTracker };
};

export const reusePackPatternEnumerated = (
	weightGroup: {
		key?: string;
		value: number;
		share: number;
		regimenDosage: number;
		numberOfPatientsInGroup: number;
		remainingDose?: number;
	},
	pack: string,
	administrationDates: Date[]
) => {
	const completePackDose = getPackDose(pack);
	const groupDosage = weightGroup.regimenDosage;
	const packsNeeded = Math.ceil(groupDosage / completePackDose);
	let availablePackDosage = weightGroup.remainingDose !== undefined ? weightGroup.remainingDose : completePackDose;
	const administeredPacks: IPackEntry[] = [];
	for (const administrationDate of administrationDates) {
		let packsOpenedNow = 0;
		if (availablePackDosage > groupDosage) {
			availablePackDosage -= groupDosage;
		} else {
			packsOpenedNow = packsNeeded;
			availablePackDosage = completePackDose - groupDosage;
		}
		const totalPacksOpened = packsOpenedNow * weightGroup.numberOfPatientsInGroup;

		administeredPacks.push({
			packName: pack,
			quantity: totalPacksOpened,
			administrationDate,
			group: [
				{
					dosePerKg: weightGroup.value,
					pack: { name: pack, quantity: packsOpenedNow, aiqSinglePatient: weightGroup.regimenDosage },
				},
			],
		});
	}

	const combinedLines = combinePacksQuantity(administeredPacks);
	const leftover = {
		weighGroup: weightGroup.key || weightGroup.value,
		remainingDosage: availablePackDosage,
	};

	return { administeredPacks: combinedLines, remainingDose: leftover };
};

export const distributePacksByWeightDosage = (
	groups: { regimenDosage: number; value: number; share: number }[],
	allPacks: string[],
	currentDate: Date,
	numberOfPatients: number,
	isWeightCohort: boolean,
	pattern: Exclude<PatternPackOptionsValue, PatternPackOptionsValue.REUSE_PACK>
) => {
	return groups.map((group) => {
		const numberOfPatientsInGroup = isWeightCohort
			? numberOfPatients
			: new Decimal(group.share).dividedBy(100).times(numberOfPatients).toNumber();
		const selectPatternFunction = patternFunctions[pattern];
		const administeredPacks = selectPatternFunction(group.regimenDosage, allPacks, currentDate, numberOfPatientsInGroup, group.value);

		return {
			value: group.value,
			administeredPacks,
			regimenDosage: group.regimenDosage,
			sharePercentage: group.share,
			numberOfPatientsInGroup,
		};
	});
};

export const sortPacks = (packs: string[], descending?: boolean) => {
	if (descending) {
		return packs.sort((a, b) => getPackDose(b) - getPackDose(a));
	}

	return packs.sort((a, b) => getPackDose(a) - getPackDose(b));
};

export const getPackDose = (pack: string) => {
	return +pack.split(" ")[0];
};

export const combinePacksQuantity = (packs: Array<IPackEntry>): IPackEntry[] => {
	const packNameCounts = new Map<string, IPackEntry>();

	packs.forEach(({ packName, quantity, administrationDate, waste, group }) => {
		const key = `${packName}-${administrationDate.toISOString()}`;
		if (!packNameCounts.has(key)) {
			packNameCounts.set(key, { packName, quantity, administrationDate, waste, group });
		} else {
			const existingPack = packNameCounts.get(key)!;
			existingPack.quantity += quantity;
			existingPack.waste = (existingPack.waste || 0) + (waste || 0);
			if (existingPack.group && group) {
				existingPack.group.push(group[0]);
			}
		}
	});

	return Array.from(packNameCounts.values());
};

// Closest Match
export const choosePacksForWeightGroupClosestPattern = (
	dosage: number,
	packs: string[],
	currentDate: Date,
	numberOfPatientsInGroup: number,
	dosePerKg: number
) => {
	const selectedPacks = [];
	const sortedPacks = sortPacks(packs);
	let dosageToCover = dosage;

	while (dosageToCover > 0) {
		const { index } = sortedPacks.reduce(
			(acc, _, i) => {
				const packDosage = getPackDose(sortedPacks[i]);
				const difference = Math.abs(dosageToCover - packDosage);

				if (difference <= acc.diff) {
					return { index: i, diff: difference };
				}

				return acc;
			},
			{ index: -1, diff: Number.MAX_SAFE_INTEGER }
		);

		const selectedPackDose = getPackDose(sortedPacks[index]);
		const wastePerPack = dosageToCover - selectedPackDose < 0 ? Math.abs(dosageToCover - selectedPackDose) : 0;
		dosageToCover -= selectedPackDose;
		const wastePerGroup = wastePerPack * numberOfPatientsInGroup;

		selectedPacks.push({
			packName: sortedPacks[index],
			quantity: 1,
			administrationDate: currentDate,
			waste: toFixedNumber(wastePerGroup), // waste per all pack in administration
		});
	}

	const uniquePacks = combinePacksQuantity(selectedPacks);

	const result = uniquePacks.map((pack) => ({
		...pack,
		quantity: pack.quantity * numberOfPatientsInGroup,
		group: [{ dosePerKg, pack: { name: pack.packName, quantity: pack.quantity } }],
	}));

	return result;
};

// Smallest Above
export const choosePacksForWeightGroupSmallestPattern = (
	dosage: number,
	packs: string[],
	currentDate: Date,
	numberOfPatientsInGroup: number,
	dosePerKg: number
) => {
	const selectedPacks: IPackEntry[] = [];
	const sortedPacks = sortPacks(packs);
	let dosageToCover = dosage;

	for (const pack of sortedPacks) {
		const packDose = getPackDose(pack);
		if (packDose >= dosageToCover) {
			const waste = packDose - dosageToCover;
			dosageToCover -= packDose;
			selectedPacks.push({
				packName: pack,
				quantity: 1,
				administrationDate: currentDate,
				waste: waste,
			});
			break;
		}
	}

	while (dosageToCover > 0) {
		const pack = sortedPacks[sortedPacks.length - 1];
		const packDose = getPackDose(pack);
		const waste = Math.max(packDose - dosageToCover, 0);

		dosageToCover -= packDose;

		selectedPacks.push({
			packName: pack,
			quantity: 1,
			administrationDate: currentDate,
			waste: waste,
		});

		for (const pack of sortedPacks) {
			const packDose = getPackDose(pack);
			if (packDose >= dosageToCover) {
				const waste = packDose - dosageToCover;
				dosageToCover -= packDose;
				selectedPacks.push({
					packName: pack,
					quantity: 1,
					administrationDate: currentDate,
					waste: waste,
				});
				break;
			}
		}
	}
	const combined = combinePacksQuantity(selectedPacks);

	const result = combined.map((p) => ({
		...p,
		quantity: p.quantity * numberOfPatientsInGroup,
		waste: toFixedNumber(p.waste ? p.waste * numberOfPatientsInGroup : 0), // waste per all pack in administration
		group: [{ dosePerKg, pack: { name: p.packName, quantity: p.quantity } }],
	}));

	return result;
};

export const choosePacksForWeightGroupBiggestPattern = (
	dosage: number,
	packs: string[],
	currentDate: Date,
	numberOfPatientsInGroup: number,
	dosePerKg: number
): IPackEntry[] => {
	const selectedPacks: IPackEntry[] = [];
	const sortedPacks = sortPacks(packs, true);
	let dosageToCover = dosage;

	while (dosageToCover > 0) {
		let foundSmallerProduct = false;
		for (const pack of sortedPacks) {
			const productValue = getPackDose(pack);
			if (productValue <= dosageToCover) {
				dosageToCover -= productValue;
				const existingIndex = selectedPacks.findIndex((p) => p.packName === pack);
				if (existingIndex !== -1) {
					selectedPacks[existingIndex].quantity++;
				} else {
					selectedPacks.push({
						packName: pack,
						quantity: 1,
						administrationDate: currentDate,
						waste: 0,
					});
				}
				foundSmallerProduct = true;
				break;
			}
		}

		if (!foundSmallerProduct) {
			const smallestPack = sortedPacks[sortedPacks.length - 1];
			const smallestPackValue = getPackDose(smallestPack);
			const remainder = dosageToCover;
			const wasteForThisPack = smallestPackValue - remainder;
			const smallestIdx = selectedPacks.findIndex((p) => p.packName === smallestPack);
			if (smallestIdx !== -1) {
				selectedPacks[smallestIdx].quantity++;
				selectedPacks[smallestIdx].waste = (selectedPacks[smallestIdx].waste || 0) + wasteForThisPack;
			} else {
				selectedPacks.push({
					packName: smallestPack,
					quantity: 1,
					administrationDate: currentDate,
					waste: wasteForThisPack,
				});
			}
			dosageToCover = 0;
		}
	}

	const combined = combinePacksQuantity(selectedPacks);
	const result = combined.map((p) => ({
		...p,
		quantity: p.quantity * numberOfPatientsInGroup,
		waste: toFixedNumber((p.waste || 0) * numberOfPatientsInGroup),
		group: [{ dosePerKg, pack: { name: p.packName, quantity: p.quantity } }],
	}));

	return result;
};
const patternFunctions = {
	[PatternPackOptionsValue.BIGGEST_BELOW]: choosePacksForWeightGroupBiggestPattern,
	[PatternPackOptionsValue.CLOSEST]: choosePacksForWeightGroupClosestPattern,
	[PatternPackOptionsValue.SMALLEST_ABOVE]: choosePacksForWeightGroupSmallestPattern,
};

export const patternAddDuration = {
	[TimeUnitOption.YEAR]: addYears,
	[TimeUnitOption.MONTH]: addMonths,
	[TimeUnitOption.WEEK]: addWeeks,
	[TimeUnitOption.DAY]: addDays,
};

export const sumPacks = (packs: { administeredPacks: IPackEntry[] }[]): IPackEntry[] => {
	const administeredPacks = packs.flatMap((g) => g.administeredPacks);
	const accum: Record<string, IPackEntry> = {};

	administeredPacks.forEach(({ packName, quantity, administrationDate, waste, group }) => {
		const key = `${packName}-${administrationDate.toISOString()}`;
		if (!accum[key]) {
			accum[key] = { packName, quantity, administrationDate, waste, group };
		} else {
			accum[key].quantity += quantity;
			accum[key].waste = (accum[key].waste ?? 0) + (waste ?? 0);
			if (accum[key].group && group) {
				accum[key].group?.push(group[0]);
			}
		}
	});

	return Object.values(accum);
};

export const defineLastAdministration = (period: Date | null, startOfNewMaintenance: boolean, lastAdministration?: Date) => {
	if (!period) return lastAdministration;
	if (startOfNewMaintenance) return undefined;
	if (period && lastAdministration) {
		const isLastBigger = isAfter(lastAdministration, period);

		return isLastBigger ? lastAdministration : period;
	}

	return period;
};

export const getAdministrationDate = (
	lastAdministration: number | Date | undefined,
	startOfDistribution: Date | null | undefined,
	currentDate: Date,
	scheduleValue: number
): Date => {
	if (lastAdministration) {
		if (isSameMonth(lastAdministration, currentDate)) {
			return addDays(lastAdministration, 1);
		}
		if (startOfDistribution && !isBefore(startOfDistribution, currentDate)) {
			return currentDate;
		}

		return addMonths(lastAdministration, scheduleValue);
	}

	return currentDate;
};

export const distributePackForInduction = (
	props: IDistributePacksArgs
): [IPackEntry[] | null, IReusablePackTracker[] | undefined, Date | null, Date | null] => {
	const {
		cohortStartDate,
		treatmentInduction,
		treatmentStartMonth,
		currentMonth,
		currentDate,
		lastAdministration,
		averageWeightPerGroup,
		allPacks,
		numberOfPatients,
		endOfTreatment,
		evaluationThresholds,
		isWeightCohort,
		previousMonthPackTracker,
	} = props;
	if (!props.treatmentInduction) {
		return [null, undefined, null, null];
	}

	const { duration, schedule } = treatmentInduction;
	const isOnceAndEver = duration.operator === RegimenDurationOption.EVER;

	if (isOnceAndEver) {
		const { distributedPackForFirstDuration, reusablePackTracker } = createPackEntryForFirstDuration({
			currentDate,
			lastAdministration,
			treatmentRegimen: treatmentInduction,
			averageWeightPerGroup,
			allPacks,
			numberOfPatients,
			isWeightCohort,
			previousMonthPackTracker,
		});

		return [distributedPackForFirstDuration, reusablePackTracker, null, null];
	}

	const startOfMaintenancePeriod = patternAddDuration[duration.unit](cohortStartDate, +duration.value);
	const endOfInductionPeriod = subDays(startOfMaintenancePeriod, 1);
	const isOnce = schedule.operator === RegimenScheduleOption.ONCE;
	const endDate = endOfTreatment ? min([endOfInductionPeriod, endOfTreatment]) : endOfInductionPeriod;
	if (isOnce && treatmentStartMonth === currentMonth) {
		const discontinuedPatients = getDiscontinuedPatients({
			startMonth: cohortStartDate,
			patientsReceivingTreatment: numberOfPatients,
			durationInMonths: 0,
			evaluationThresholds,
		});
		const actualPatients = discontinuedPatients ? discontinuedPatients : numberOfPatients;

		const { distributedPackForFirstDuration, reusablePackTracker } = createPackEntryForFirstDuration({
			currentDate,
			lastAdministration,
			treatmentRegimen: treatmentInduction,
			averageWeightPerGroup,
			allPacks,
			numberOfPatients: actualPatients,
			isWeightCohort,
			previousMonthPackTracker,
		});

		return [distributedPackForFirstDuration, reusablePackTracker, endOfInductionPeriod, startOfMaintenancePeriod];
	}

	if (!isOnce && !isAfter(currentDate, endOfInductionPeriod)) {
		const { distributedPackForEveryDuration, reusablePackTracker } = createPackEntryForEveryDuration({
			currentDate,
			lastAdministration,
			treatmentRegimen: treatmentInduction,
			averageWeightPerGroup,
			allPacks,
			numberOfPatients,
			endDate,
			cohortStartDate,
			evaluationThresholds,
			isWeightCohort,
			previousMonthPackTracker,
		});

		return [distributedPackForEveryDuration, reusablePackTracker, endOfInductionPeriod, startOfMaintenancePeriod];
	}

	return [null, undefined, endOfInductionPeriod, startOfMaintenancePeriod];
};

export const getLastTreatmentDay = (availableDaysForTreatment: undefined | number, cohortStartDate: Date) => {
	if (!availableDaysForTreatment) {
		return undefined;
	}

	if (availableDaysForTreatment <= 1) {
		return cohortStartDate;
	}

	return addDays(cohortStartDate, availableDaysForTreatment - 1);
};

const getAverageGroupWeight = (patientWeight?: IPatientWeightInput[], weightCohort?: number) => {
	if (!patientWeight) {
		return [];
	}

	if (weightCohort) {
		return getPatientWeightFoDosagePurpose([patientWeight[weightCohort - 1]]);
	}

	return getPatientWeightFoDosagePurpose(patientWeight);
};

export const getDistributedPacks = (
	args: IGetDistributedPacksArgs
): { distributedPacks: Array<IDistributedPack>; lastAdministration?: Date; reusablePackTracker: IReusablePackTracker[] } => {
	const {
		currentDate,
		lastAdministration,
		numberOfPatients,
		patientSplitId,
		receivingTreatment,
		currentMonth,
		treatmentStartMonth,
		sectionId,
		indicationId,
		context,
		simulationStarted,
		availableDaysForTreatment,
		previousMonthPackTracker,
	} = args;

	const { productPricing, patientWeight, treatmentRegimen } = context.getTreatmentServiceInput(sectionId, indicationId);
	const isWeightCohort = sectionId.split(GROUP_LABEL)[1];
	const allPacks = productPricing.map((product) => product.pack);
	const distributedPacks: Array<IPackEntry> = [];
	const groupedTreatments = groupBy(treatmentRegimen, (treatment) => treatment.index);
	const cohortStartDate = addMonths(new Date(simulationStarted), treatmentStartMonth);
	const averageWeightPerGroup = getAverageGroupWeight(patientWeight, +isWeightCohort).map((group) => ({
		...group,
		key: `${group.value}-${cohortStartDate.getMonth()}-${cohortStartDate.getDay()}`,
	}));
	const weightGroupName = isWeightCohort ? constructNameForWeightGroup(patientWeight[+isWeightCohort - 1]) : undefined;
	const endOfTreatment = getLastTreatmentDay(availableDaysForTreatment, cohortStartDate);
	const evaluationThresholds = context.getEvaluationThresholds(sectionId);
	const isSingleDoseTreatment = treatmentRegimen[0].duration.operator === RegimenDurationOption.EVER;
	let currentPackTracker: IReusablePackTracker[] | undefined = [];
	if (numberOfPatients > 0) {
		groupedTreatments.forEach((treatments) => {
			const [treatmentInduction, treatmentMaintenance] = partition(treatments, (treatment) =>
				[RegimenDurationOption.FIRST, RegimenDurationOption.EVER].includes(treatment.duration?.operator)
			);
			const [distributedInduction, inductionTracker, endOfInductionPeriod, startOfMaintenancePeriod] = distributePackForInduction({
				cohortStartDate,
				treatmentInduction: treatmentInduction[0],
				treatmentStartMonth,
				currentMonth,
				currentDate,
				lastAdministration,
				averageWeightPerGroup,
				allPacks,
				numberOfPatients,
				endOfTreatment,
				evaluationThresholds,
				isWeightCohort: !!isWeightCohort,
				previousMonthPackTracker,
			});
			currentPackTracker = inductionTracker;

			if (distributedInduction) {
				distributedPacks.push(...distributedInduction);
			}
			const isMaintenancePeriod =
				startOfMaintenancePeriod &&
				(isSameMonth(currentDate, startOfMaintenancePeriod) || isAfter(currentDate, startOfMaintenancePeriod));
			const isMaintenanceBeginMonth = startOfMaintenancePeriod && isSameMonth(currentDate, startOfMaintenancePeriod);
			if ((!treatmentInduction[0] || isMaintenancePeriod) && treatmentMaintenance[0]) {
				const { distributedPackForEveryDuration, reusablePackTracker } = createPackEntryForEveryDuration({
					currentDate: isMaintenanceBeginMonth && startOfMaintenancePeriod ? startOfMaintenancePeriod : currentDate,
					lastAdministration: defineLastAdministration(endOfInductionPeriod, !!isMaintenanceBeginMonth, lastAdministration),
					endDate: endOfTreatment,
					treatmentRegimen: treatmentMaintenance[0],
					averageWeightPerGroup,
					allPacks,
					startOfDistribution: startOfMaintenancePeriod,
					numberOfPatients,
					cohortStartDate,
					evaluationThresholds,
					isWeightCohort: !!isWeightCohort,
					previousMonthPackTracker: previousMonthPackTracker || currentPackTracker,
				});
				currentPackTracker = reusablePackTracker;
				if (distributedPackForEveryDuration && distributedPackForEveryDuration.length) {
					Array.prototype.push.apply(distributedPacks, distributedPackForEveryDuration);
				}
			}
		});
	}

	const finalPacks = distributedPacks.map((p, idx) => {
		const basePrice = getTreatmentPrice(p.packName, p.administrationDate, productPricing);
		const updatedPack = {
			...p,
			group: p.group?.map((g) => ({ ...g, weightGroupName })),
		};

		const { discounts } = getFinancialData({
			treatmentPrice: basePrice,
			packAdministrationDate: p.administrationDate,
			packName: p.packName,
			receivingTreatment,
			quantity: p.quantity,
			patientSplitId,
			cohortStartDate,
			treatmentDuration: args.treatmentDuration,
			distributedPack: p,
			currentDate,
			currentMonth,
			sectionId,
			context,
			numberOfPatients,
			isSingleDoseTreatment,
			isFirstDistributionInAMonth: idx === 0,
			weightGroupName,
		});

		const packsPerPatient = Math.round(p.quantity / numberOfPatients);

		return {
			...updatedPack,
			basePrice,
			packsPerPatient,
			discounts,
		};
	});

	return {
		distributedPacks: finalPacks,
		lastAdministration: getLastElement(distributedPacks)?.administrationDate,
		reusablePackTracker: currentPackTracker || [],
	};
};
